'use client'

import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { FC, SyntheticEvent } from 'react'

import { useFormatPrice } from '@src/blocks/currency'
import RoundAddButton from '@src/components/product-card/icons/roundAddButton.inline.svg'
// Reuse the legacy buy-now button's icon styles verbatim (generic round icon-button — nothing
// color/legacy-coupled); positioning + hover-reveal come from the card's own `.addToBag` class,
// passed in via `className`.
import buyItNowStyles from '@src/components/product-card/product-card-actions/buy-it-now/buy-it-now.module.scss'
import type { ProductCardTag } from '@src/components/product-card/product-card.types'
import { IconButton } from '@src/quince-components/src'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import type { SvdNativeCardVariant, SvdNativeProduct } from '@src/types/svd-product-card.types'

import { openSvdQuickAdd } from './svd-quick-add'

// @i18n-ignore
const ADD_TO_BAG_TEST_ID = 'svd-add-to-bag'

/**
 * SVD-native quick-add (bag) button — the grid-card surface trigger. Self-contained (Option 2): no
 * fork of the legacy `ProductCardBuyItNow`; consumes `SvdNativeProduct`/`SvdNativeCardVariant` directly
 * and delegates the actual open to the shared `openSvdQuickAdd` (Path A: existing enrich modal).
 *
 * The card decides whether to render this (gates on `flags.quickBuyEnabled`, non-bundle, a resolvable
 * `spid`, not-native-view) and owns the positioning/hover-reveal via the `className` it passes.
 * `stopPropagation` keeps a click off the sibling `<Link>` so the card doesn't navigate.
 */
export const SvdCardAddToBag: FC<{
  product: SvdNativeProduct
  selectedVariant?: SvdNativeCardVariant
  applicableTags: ProductCardTag[]
  productClickTrackConfig?: TrackConfig
  productPosition?: number
  searchQuery?: string
  /** Positioning + hover-reveal class from the card. */
  className?: string
  /** Escape hatch for reco/cart surfaces (open their own flow instead of the modal). */
  override?: () => void
}> = ({
  product,
  selectedVariant,
  applicableTags,
  productClickTrackConfig,
  productPosition,
  searchQuery,
  className,
  override,
}) => {
  const { t } = useTranslation('common')
  const formatPrice = useFormatPrice()

  const handleClick = (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    openSvdQuickAdd({
      product,
      selectedVariant,
      applicableTags,
      trackConfig: productClickTrackConfig,
      productPosition,
      searchQuery,
      override,
    })
  }

  const anchorName = selectedVariant?.displayConfig?.displayName
  const productName = anchorName ? `${anchorName} ${product.title}` : product.title
  const price = selectedVariant?.price.salePrice
  const ariaLabel = price
    ? t('pc_buy_now_price_aria', { productName, price: formatPrice(price) })
    : t('pc_buy_now_aria', { productName })

  return (
    <IconButton
      className={classnames(buyItNowStyles.addToCart, className)}
      iconClassName={buyItNowStyles.icon}
      onClick={handleClick}
      iconSvg={RoundAddButton}
      ariaLabel={ariaLabel}
      dataTestId={ADD_TO_BAG_TEST_ID}
    />
  )
}
